/**
 * German catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 1.
 *
 * `dir: 'ltr'`. Native-grade German at a mathematician's register. **Warrant → „Berechtigung"**
 * — the epistemic-entitlement reading (Plantinga-style „warrant") rather than Toulmin's
 * „Schlussregel"; chosen because the axis is a graded *justification strength*. See the
 * glossary in `Agent9_REPORT.md` for the flagged alternatives.
 *
 * W/M grades stay Latin (`::numbering-system/latn`) — a grade is a stable id, not prose.
 * German is a high-expansion language (strings run long); the layout test confirms the
 * longest strings still lay out cleanly. CLDR cardinal categories are one / other, so
 * `frameActive` declares exactly those (plus an explicit `=0`). Per INV-STABLE-ID a wrong
 * translation is a presentation concern only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const DE_CATALOG = {
    locale: 'de',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Untersuchung',
        [P2.sectionClaims]: 'Behauptungen',
        [P2.sectionIntegrity]: 'Integrität',
        [P2.axisWarrantLabel]: 'Berechtigung',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empirisch',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Definition} interp {Interpretation} testable {Testbar} tested {Getestet} refuted {Widerlegt} program {Forschungsprogramm} philos {Philosophisch} other {Unbekannt}}',
        [P2.axisModalLabel]: 'Modal',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Stärkegrad {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Erforschbar',
        [P2.stateRefuted]: 'Widerlegt',
        [P2.stateOutside]: 'Außerhalb des Geltungsbereichs',
        [P2.verdictConflict]: 'Steht im Konflikt mit {constraints}',
        [P2.verdictOutside]: 'Benötigt {missing}',
        [P2.lossRegisterPlain]: 'In einfache Sprache vereinfacht — ein Teil der Präzision entfällt.',
        [P2.lossRegisterKid]: 'Für jüngere Leser vereinfacht — viele Details entfallen.',
        [P2.lossProfileDetail]: 'Das vollständige Profil aus Berechtigung / Empirisch / Modal wird hier zusammengefasst.',
        [P2.lossConflictDetail]: 'Die Konfliktdetails sind in dieser Ansicht gekürzt.',
        [P2.laneVerified]: 'Verifiziert',
        [P2.laneUnverified]: 'Nicht verifiziert',
        [P2.laneConflict]: 'Konflikt nachgewiesen',
        [P2.constraintLogic]: 'Logik',
        [P2.constraintLightBends]: 'Licht wird in einem Gravitationsfeld abgelenkt',
        [P2.constraintParallelPostulate]: 'Das Parallelenpostulat',
        [P2.claimScalarGravity]: 'Skalare Gravitationstheorie',
        [P2.claimNonEuclidean]: 'Nichteuklidische Geometrie',
        [P2.claimSubstrateSr]: 'Substrat-Interpretation der speziellen Relativitätstheorie',
        [P2.edgeExtObservation]: 'Externe Beobachtung',
        [P2.edgeResourceSample]: 'Gemeinsame Rechenressource',
        [P2.edgeHeuristicSample]: 'Heuristische Anleitung',
        [P2.edgeSharedSample]: 'Gemeinsame Grundlage',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Einschränkungen',
        [P2.sectionEdges]: 'Kanten',
        [P2.sectionFrame]: 'Rahmen',
        [P2.sectionExposure]: 'Exposition',
        [P2.statusAdoptedFormal]: 'Übernommen (formal)',
        [P2.statusAdoptedInterpretive]: 'Übernommen (interpretativ)',
        [P2.statusSuspended]: 'Ausgesetzt',
        [P2.edgeEffectConstraint]: 'Bildet eine Einschränkung (kann widerlegen)',
        [P2.edgeEffectResource]: 'Ressource — keine Auswirkung auf Urteile',
        [P2.edgeEffectHeuristic]: 'Heuristik — keine Auswirkung auf Urteile',
        [P2.edgeEffectShared]: 'Gemeinsame Grundlage — keine Auswirkung auf Urteile',
        [P2.frameActive]: '{count, plural, =0 {Keine aktiven Einschränkungen} one {# aktive Einschränkung} other {# aktive Einschränkungen}}',
        [P2.exposureNone]: 'Keine interpretativen Abhängigkeiten',
        [P2.exposureDep]: 'Hängt von {id} ab',
        [P2.laneTrackMachine]: 'Maschine',
        [P2.laneTrackExpert]: 'Experte',
        [P2.laneTrackCommunity]: 'Gemeinschaft',
        [P2.expertAudited]: 'Auditiert',
        [P2.expertInProgress]: 'In Bearbeitung',
        [P2.expertNotReviewed]: 'Nicht geprüft',
        [P2.communityFlags]: 'Meldungen vorhanden',
        [P2.communityNone]: 'Keine Meldungen',
        [P2.controlChallenge]: 'Anfechten',
        [P2.controlActivate]: 'Aktivieren',
        [P2.controlMakeInterpretive]: 'Interpretativ machen',
        [P2.controlImportEdge]: 'Kante importieren',
        [P2.controlPromote]: 'Hochstufen',
        [P2.controlUndo]: 'Rückgängig',
        [P2.controlRedo]: 'Wiederherstellen',
        [P2.controlRegister]: 'Registrieren',
        [P2.promoteRejected]: 'Hochstufung abgelehnt',
        [P2.promoteAccepted]: 'Hochstufung angenommen',
        [P2.promoteReasonRefutationTerminal]: 'Ein Beweis kann eine Widerlegung nicht heilen.',
        [P2.promoteReasonKeystone]: 'Eine Schlussstein-Hochstufung benötigt empirische Unterstützung.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Die empirische Art ist nicht festgelegt.',
        [P2.promoteReasonModalNotFixed]: 'Der modale Grad ist nicht festgelegt.',
        [P2.promoteReasonWarrantNotImproved]: 'Der Berechtigungsgrad ist nicht verbessert.',
        [P2.evidenceComputation]: "{engine} hat {op} = {result} unter {config} berechnet",
        [P2.lossNumericRounding]: "Ergebnis auf {dp} Dezimalstellen gerundet",
    },
};
//# sourceMappingURL=de.js.map